import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { dateFormat } from 'shared/util'
import { SisetuResult } from 'interface/sisetu'
import { SisetuCommentResult, useDeleteSisetuComment, useListSisetuComment, usePostSisetuComment } from 'interface/sisetu/comment'

import {
  Stack, Button,
  Divider,
  useTheme,
  useMediaQuery,
  Chip,
  Box,
  StackProps
} from "@mui/material"
import { CloseIcon, OkIcon } from "shared/icons"

import { useUserSession } from "features/session/context"
import { ConfirmDialog } from "components/dialog/confirm"
import { MarkdownInput } from "components/controls/markDownInput"
import { Loading } from 'components/controls/loading'

import { SisetuCommentRecord } from "./record"

type SisetuCommentBlockProps = {
  sisetu: SisetuResult
  onClose?: () => void
} & StackProps
export const SisetuCommentBlock = ({
  sisetu,
  onClose,
  ...others
}: SisetuCommentBlockProps) => {
  const { userId, isGeneral } = useUserSession()
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down('sm'))
  const bottomRef = useRef<HTMLDivElement>(null)

  const [postData, setPostData] = useState<string>('')
  const [editTarget, setEditTarget] = useState<SisetuCommentResult | null>(null)
  const [removeTarget, setRemoveTarget] = useState<SisetuCommentResult | null>(null)

  const commentListState = useListSisetuComment(sisetu.sisetuCode)
  const commentList = useMemo(
    () => {
      const commentList = commentListState.data?.list ?? []
      commentList.sort((a, b) => a.createTime.localeCompare(b.createTime))
      return commentList
    },
    [commentListState]
  )
  useEffect(
    () => {
      bottomRef.current?.scrollIntoView()
    },
    [commentList]
  )


  const onPosted = useCallback(
    () => {
      setPostData('')
      commentListState.refetch()
    },
    [setPostData, commentListState]
  )  
  const [postState, postComment] = usePostSisetuComment(sisetu.sisetuCode, { onPosted })
  const onPostComment = useCallback(
    () => postComment({ sisetuComment: postData }),
    [postComment, postData]
  )
  const onEditted = useCallback(
    () => {
      setEditTarget(null)
      commentListState.refetch()
    },
    [setEditTarget, commentListState]
  )

  const onDeleted = useCallback(
    () => {
      setRemoveTarget(null)
      commentListState.refetch()
    },
    [setRemoveTarget, commentListState]
  )
  const [removeCommentState, removeComment] = useDeleteSisetuComment(sisetu.sisetuCode, { onDeleted })

  return (
    <>
      <Stack
        direction="column"
        overflow="hidden"
        width="100%"
        height="100%"
        boxSizing="border-box"
        {...others}
      >
        <Stack
          flexGrow={1}
          direction="column"
          padding={1}
          spacing={1}
          width="100%"
          boxSizing="border-box"
          overflow="auto"
          sx={{
            backgroundColor: 'GhostWhite'
          }}
        >
          {commentList.map((comment, i, arr) => {
            const prev = i > 0 ? arr[i-1] : null
            return (
              <>
                {(!prev || prev.createTime.substring(0, 10) !== comment.createTime.substring(0, 10)) && (
                  <Box
                    key={`date-${comment.createTime.substring(0, 10)}`}
                    textAlign="center"
                    position="sticky"
                    top={1}
                    zIndex={1}
                  >
                    <Chip
                      size="small"
                      color="info"
                      label={`${dateFormat(comment.createTime, { wareki: false })}`}
                      sx={{
                        whiteSpace: "nowrap",
                        paddingX: 2,
                        marginLeft: "auto",
                        marginRight: "auto"
                      }}
                    />
                  </Box>
                )}
                <SisetuCommentRecord
                  key={`comment-${comment.commentId}`}
                  sisetuCode={sisetu.sisetuCode}
                  isGeneral={isGeneral}
                  isSmallMedia={isSmallMedia}
                  isEdit={comment.commentId === editTarget?.commentId}
                  userId={userId}
                  comment={comment}
                  onEdit={setEditTarget}
                  onEditCancel={() => setEditTarget(null)}
                  onEditted={onEditted}
                  onRemove={setRemoveTarget}
                />
              </>
            )
          })}
          <div ref={bottomRef} />
        </Stack>

        <Divider/>

        <Stack direction="column" spacing={1} padding={1}>
          {postState.posting && <Loading/>}
          <MarkdownInput
            size="small"
            value={postData}
            onChange={e => setPostData(e.target.value)}
            rows={5}
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              variant="contained"
              size="small"
              loading={postState.posting}
              onClick={onPostComment}
              startIcon={<OkIcon/>}
            >
              保存
            </Button>
            {onClose && (
              <Button
                variant="text"
                size="small"
                onClick={onClose}
                startIcon={<CloseIcon/>}
              >
                閉じる
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>

      {removeTarget ? (
        <ConfirmDialog
          isLoading={removeCommentState.posting}
          message={`コメントを削除します。よろしいですか？`}
          ok={{
            label: 'はい',
            onClick: () => {
              if (!removeTarget?.commentId) return
              removeComment(removeTarget.commentId)
            }
          }}
          cancel={{
            label: 'いいえ',
            onClick: () => setRemoveTarget(null)
          }}
        />
      ) : <></>}
    </>
  )
}